import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { Box } from "@material-ui/core";
import { LoadsAction } from "actions/loads";
import { TransporterRoleAction } from "actions/auth";
import { setSetting } from "actions/settings";
import AgGridTable from "components/AgGrid/AgGridContainer";
import TableWrapper from "components/AgGrid/TableWrapper";
import { Column as ColumnLayout } from "components/Layout";
import { SmallText, TitleText } from "components/Text";
import { AgGridTables } from "constants/aggrid";
import { Column } from "constants/columns";
import { GlobalSearchFields } from "constants/globalSearch";
import { Settings } from "constants/settings";
import { useBoolean } from "hooks";
import { BaseColumnDefinitions } from "lib/agGrid/columnDefinitions";
import {
  dateXDaysAgo,
  formatDateTimeString,
  formatISO8601DateString,
} from "lib/timeFormats";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFilteredLoadsForAgGrid,
  selectLoadsBuyerOptions,
} from "selectors/loads";
import { getActiveRole, getSetting, getTransporterFilterData } from "selectors";
import styled from "styled-components/macro";
import { SmallFaIcon } from "components/FaIcon";
import {
  faLightbulb,
  faRotateReverse,
} from "@fortawesome/pro-regular-svg-icons";
import { getDateRangeLabel } from "components/DateRangePicker/DateRangeRenderer";
import { ColDef } from "ag-grid-community";
import { rollUp, averageWeightAggFunction } from "lib/agGrid/aggregators";
import { addDays } from "date-fns";
import { OptionsSearch } from "components/SearchInput/GlobalSearch";
import { isEmpty } from "lodash";
import { FilteringModal } from "./FilterForm";

const colDefs: ColDef<Loads>[] = [
  BaseColumnDefinitions[Column.Loads.AVERAGE_GRAMS],
  {
    headerName: "Buyer",
    children: [BaseColumnDefinitions[Column.Loads.BUYER_NAME]],
  },
  {
    headerName: "Buyer Way",
    children: [BaseColumnDefinitions[Column.Loads.BUYER_WAY_NAME]],
  },
  BaseColumnDefinitions[Column.Loads.CREATED],
  BaseColumnDefinitions[Column.Loads.LAST_MODIFIED],
  BaseColumnDefinitions[Column.Loads.DELIVERY_PEN],
  BaseColumnDefinitions[Column.Loads.DESTINATION_PIC],
  BaseColumnDefinitions[Column.Loads.LOAD_STATUS],
  {
    headerName: "Drafting Attributes",
    children: [
      BaseColumnDefinitions[Column.Loads.AGE],
      BaseColumnDefinitions[Column.Loads.BREED],
      BaseColumnDefinitions[Column.Loads.MARKS],
      BaseColumnDefinitions[Column.Loads.SALE_ROUND],
      BaseColumnDefinitions[Column.Loads.SEX],
      BaseColumnDefinitions[Column.Loads.SPECIES],
    ],
  },
  {
    headerName: "Livestock Sale",
    children: [
      BaseColumnDefinitions[Column.Loads.LIVESTOCK_SALE_CODE],
      BaseColumnDefinitions[Column.Loads.LIVESTOCK_SALE_DATE],
      BaseColumnDefinitions[Column.Loads.LIVESTOCK_SALE_ID],
      BaseColumnDefinitions[Column.Loads.LIVESTOCK_SALE_NAME],
    ],
  },
  BaseColumnDefinitions[Column.Loads.PROGENY],
  BaseColumnDefinitions[Column.Loads.QUANTITY],
  BaseColumnDefinitions[Column.Loads.QUANTITY_DELIVERED],
  BaseColumnDefinitions[Column.Loads.QUANTITY_WEIGHED],
  BaseColumnDefinitions[Column.Loads.SALE_LOT_STATUS],
  BaseColumnDefinitions[Column.Loads.SALEYARD_NAME],
  BaseColumnDefinitions[Column.Loads.THIRD_PARTY],
  BaseColumnDefinitions[Column.Loads.TOTAL_MASS_GRAMS],
  BaseColumnDefinitions[Column.Loads.TRANSPORTER_NAME],
];

// Stable function references for ag-grid props
const getRowId = (params: { data: { id: string } }) => params.data.id;
const isGroupOpenByDefault = (params: {
  rowGroupColumn?: { getColId: () => string };
}) => {
  // Collapse these column groups by default, expand all others
  const colId = params.rowGroupColumn?.getColId();
  const collapsedFields = [
    BaseColumnDefinitions[Column.Loads.BUYER_NAME].field,
    BaseColumnDefinitions[Column.Loads.BUYER_WAY_NAME].field,
  ];
  return !collapsedFields.includes(colId);
};
const aggFuncs = { rollUp, averageWeightAggFunction };

const LoadsTableWrapper = styled(TableWrapper).attrs({
  className: "loads-table",
})``;

export const DateRangeTextWrapper = styled.p`
  ${({ theme }) => `
  background-color: ${theme.colors.collapseBackground};
  padding: ${theme.space[1]}px;
  margin-right: auto;
  margin-left: auto;
  margin-top: ${theme.space[0]}px;
  margin-bottom: ${theme.space[0]}px;
  border-radius: ${theme.space[1]}px;
  align-self: center;
  ${({ fullWidth }) => fullWidth && "width: 100%"}
   `}
`;

const LOADS_FILTER_SETTING = Settings.loadsFilter;

// Mapping from GlobalSearchFields to backend filterData keys
// This allows easy extension when new filters are added
const FILTER_FIELD_MAPPING = {
  [GlobalSearchFields.Buyer]: "buyerIds",
  // Future fields can be added here:
  // [GlobalSearchFields.Vendor]: 'vendorIds',
} as const;

export function LoadTable(): React.JSX.Element {
  const [isFiltersShowing, showFilters, hideFilters] = useBoolean(false);
  const [filters, setFilters] = useState({
    pickupDateGte: formatISO8601DateString(dateXDaysAgo(6)),
    pickupDateLte: formatISO8601DateString(addDays(new Date(), 1)),
  });
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const dispatch = useDispatch();
  const activeRole = useSelector(getActiveRole);
  const transporterFilterData = useSelector(getTransporterFilterData);
  const buyerOptions = useSelector(selectLoadsBuyerOptions);
  const loads = useSelector(selectFilteredLoadsForAgGrid);
  const currentFilter = useSelector(getSetting(LOADS_FILTER_SETTING));

  // Helper to update filter_data in backend when filter changes
  const updateFilterData = useCallback(
    (filter: Record<string, string[]> | null) => {
      if (!activeRole?.id) {
        return;
      }

      // Map frontend filter fields to backend filterData keys
      const newFilterData = { ...transporterFilterData };
      if (filter && !isEmpty(filter)) {
        Object.entries(FILTER_FIELD_MAPPING).forEach(
          ([frontendField, backendKey]) => {
            const fieldValues = filter[frontendField];
            if (fieldValues !== undefined) {
              newFilterData[backendKey] =
                fieldValues.length > 0 ? fieldValues : null;
            }
          },
        );
      } else {
        // If filter is null, clear all mapped fields
        Object.values(FILTER_FIELD_MAPPING).forEach(backendKey => {
          newFilterData[backendKey] = null;
        });
      }
      dispatch(
        TransporterRoleAction.update({
          id: activeRole.id,
          filterData: newFilterData,
        }),
      );
    },
    [activeRole?.id, transporterFilterData, dispatch],
  );

  // Sync filter between frontend and backend
  useEffect(() => {
    // Initialize frontend filter from backend on first load
    if (currentFilter === undefined) {
      const initialFilter: Record<string, string[]> = {};
      let hasBackendData = false;

      Object.entries(FILTER_FIELD_MAPPING).forEach(
        ([frontendField, backendKey]) => {
          const backendValue = transporterFilterData[backendKey];
          if (backendValue !== undefined) {
            initialFilter[frontendField] = backendValue;
            hasBackendData = true;
          }
        },
      );

      if (hasBackendData) {
        dispatch(setSetting(LOADS_FILTER_SETTING, initialFilter));
      }
      return;
    }

    // Update backend when frontend filter changes
    const needsUpdate = Object.entries(FILTER_FIELD_MAPPING).some(
      ([frontendField, backendKey]) => {
        const frontendValue = currentFilter?.[frontendField] ?? null;
        const backendValue = transporterFilterData[backendKey] ?? null;
        return frontendValue !== backendValue;
      },
    );

    if (needsUpdate) {
      updateFilterData(currentFilter || null);
    }
  }, [currentFilter, transporterFilterData, updateFilterData, dispatch]);

  // Helper to request loads and update lastRefresh
  const requestLoads = React.useCallback(() => {
    dispatch(LoadsAction.request(filters));
    setLastRefresh(new Date());
  }, [dispatch, filters]);

  // Fetch on mount and when filters change
  useEffect(() => {
    requestLoads();
  }, [requestLoads]);

  // Fetch every minute
  useEffect(() => {
    const interval = setInterval(() => {
      requestLoads();
    }, 60000);
    return () => clearInterval(interval);
  }, [requestLoads]);

  // Format filter options for OptionsSearch
  // Currently only includes buyer, but structure supports adding more fields
  const filterOptions = useMemo(() => {
    const options = [];
    if (buyerOptions && buyerOptions.length > 0) {
      options.push({
        label: "Buyers",
        field: GlobalSearchFields.Buyer,
        options: buyerOptions,
      });
    }
    // Future fields can be added here
    return options;
  }, [buyerOptions]);

  const extraSearchComponents = useMemo(
    () =>
      filterOptions.length > 0 ? (
        <OptionsSearch
          options={filterOptions}
          settingName={LOADS_FILTER_SETTING}
          overridePlaceHolder="Filter by buyer..."
          closeMenuOnSelect={false}
        />
      ) : null,
    [filterOptions],
  );

  const extraActionComponents = useMemo(
    () => [
      <DateRangeTextWrapper key="date-range">
        <SmallFaIcon icon={faLightbulb} color="gray9C" />{" "}
        {getDateRangeLabel(
          new Date(filters.pickupDateGte),
          new Date(filters.pickupDateLte),
        )}
      </DateRangeTextWrapper>,
    ],
    [filters.pickupDateGte, filters.pickupDateLte],
  );

  const additionalActions = useMemo(
    () => [
      {
        title: (
          <ColumnLayout>
            Refresh{" "}
            {lastRefresh ? (
              <SmallText>Last: {formatDateTimeString(lastRefresh)}</SmallText>
            ) : null}
          </ColumnLayout>
        ),
        icon: faRotateReverse,
        isDisabled: false,
        onClick: requestLoads,
      },
      {
        title: "Change Filters",
        icon: faFilter,
        isDisabled: false,
        onClick: showFilters,
      },
    ],
    [lastRefresh, requestLoads, showFilters],
  );

  return (
    <ColumnLayout fullHeight>
      <Box p={2}>
        <TitleText>Loads</TitleText>
      </Box>
      <AgGridTable
        columnDefs={colDefs}
        rowData={loads}
        showGlobalSearchFilter={false}
        tableName={AgGridTables.LOADS}
        getRowId={getRowId}
        additionalActions={additionalActions}
        extraSearchComponents={extraSearchComponents}
        extraActionComponents={extraActionComponents}
        aggFuncs={aggFuncs}
        isGroupOpenByDefault={isGroupOpenByDefault}
        WrapperComponent={LoadsTableWrapper}
      />
      {isFiltersShowing && (
        <FilteringModal
          closeSelf={hideFilters}
          setFilters={setFilters}
          filters={filters}
        />
      )}
    </ColumnLayout>
  );
}
